/**
 * The search screen's state, and the shim that lets it run against either
 * shape of the search API.
 *
 * Two things live here.
 *
 * **The URL is the state.** Every filter is a search parameter, under the same
 * names the API takes (docs/developer/contracts.md §4), so a search is
 * shareable by copying the address bar and the address bar is readable by
 * anyone who knows the API. No filter is held in component state, which is
 * what makes back, forward and a pasted link all behave.
 *
 * **The shim.** The page-grouped, unit-typed search of the contract is being
 * built; the server currently still answers the older article-shaped one. The
 * adapters below map what a response actually carries onto the contract shape
 * and leave everything it does not carry null, so a field the old server has
 * no answer for renders as absent rather than as an invented value. Nothing
 * here fills a gap with a plausible number.
 */

import type {
  Facets,
  Region,
  SearchMode,
  SearchPageGroup,
  SearchResponse,
  SearchSort,
  SearchUnitResult,
  UnitType,
} from '../../lib/api'

// --- the state carried in the URL -------------------------------------------

export interface SearchState {
  q: string
  group: 'page' | 'unit'
  types: string[]
  section: string
  author: string
  publication: string
  wire: '' | 'true' | 'false'
  pipeline: '' | 'A' | 'B'
  min_confidence: string
  date_from: string
  date_to: string
  mode: SearchMode
  sort: SearchSort
  offset: number
}

export const PER_PAGE = 24

export const EMPTY_STATE: SearchState = {
  q: '',
  group: 'page',
  types: [],
  section: '',
  author: '',
  publication: '',
  wire: '',
  pipeline: '',
  min_confidence: '',
  date_from: '',
  date_to: '',
  mode: 'blend',
  sort: 'relevance',
  offset: 0,
}

const MODES: SearchMode[] = ['blend', 'exact', 'fuzzy', 'semantic', 'phonetic']
const SORTS: SearchSort[] = ['relevance', 'oldest', 'newest']

export function readState(params: URLSearchParams): SearchState {
  const types = (params.get('types') ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
  const mode = (params.get('mode') ?? 'blend') as SearchMode
  const sort = (params.get('sort') ?? 'relevance') as SearchSort
  return {
    q: params.get('q') ?? '',
    group: params.get('group') === 'unit' ? 'unit' : 'page',
    types,
    section: params.get('section') ?? '',
    author: params.get('author') ?? '',
    publication: params.get('publication') ?? '',
    wire: (params.get('wire') ?? '') as SearchState['wire'],
    pipeline: (params.get('pipeline') ?? '') as SearchState['pipeline'],
    min_confidence: params.get('min_confidence') ?? '',
    date_from: params.get('date_from') ?? '',
    date_to: params.get('date_to') ?? '',
    mode: MODES.includes(mode) ? mode : 'blend',
    sort: SORTS.includes(sort) ? sort : 'relevance',
    offset: Math.max(0, Number(params.get('offset') ?? 0) || 0),
  }
}

/** Only what differs from the default is written, so a plain search is a plain URL. */
export function writeState(state: SearchState): URLSearchParams {
  const params = new URLSearchParams()
  const put = (key: string, value: string) => {
    if (value) params.set(key, value)
  }
  put('q', state.q)
  if (state.group !== 'page') params.set('group', state.group)
  put('types', state.types.join(','))
  put('section', state.section)
  put('author', state.author)
  put('publication', state.publication)
  put('wire', state.wire)
  put('pipeline', state.pipeline)
  put('min_confidence', state.min_confidence)
  put('date_from', state.date_from)
  put('date_to', state.date_to)
  if (state.mode !== 'blend') params.set('mode', state.mode)
  if (state.sort !== 'relevance') params.set('sort', state.sort)
  if (state.offset) params.set('offset', String(state.offset))
  return params
}

/** The query string the API takes. The same names, so the two never drift. */
export function apiQuery(state: SearchState, extra: Record<string, string> = {}): string {
  const params = writeState(state)
  params.set('group', state.group)
  params.set('limit', String(PER_PAGE))
  for (const [key, value] of Object.entries(extra)) params.set(key, value)
  return params.toString()
}

/** The filters, without paging or ordering: what a facet count is conditioned on. */
export function facetQuery(state: SearchState): string {
  const params = writeState({ ...state, offset: 0 })
  params.delete('group')
  params.delete('sort')
  return params.toString()
}

// --- what is currently filtering, as chips ----------------------------------

export interface ActiveFilter {
  key: string
  label: string
  clear: (state: SearchState) => SearchState
}

export function activeFilters(state: SearchState, facets: Facets | undefined): ActiveFilter[] {
  const chips: ActiveFilter[] = []
  const labelFor = (list: { value: string; label?: string }[], value: string) =>
    list.find((item) => item.value === value)?.label ?? value

  if (state.section) {
    chips.push({
      key: 'section',
      label: labelFor(facets?.sections ?? [], state.section),
      clear: (s) => ({ ...s, section: '' }),
    })
  }
  for (const type of state.types) {
    chips.push({
      key: `type:${type}`,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      clear: (s) => ({ ...s, types: s.types.filter((value) => value !== type) }),
    })
  }
  if (state.author) {
    chips.push({ key: 'author', label: state.author, clear: (s) => ({ ...s, author: '' }) })
  }
  if (state.publication) {
    chips.push({
      key: 'publication',
      label: labelFor(facets?.publications ?? [], state.publication),
      clear: (s) => ({ ...s, publication: '' }),
    })
  }
  if (state.wire) {
    chips.push({
      key: 'wire',
      label: state.wire === 'true' ? 'Wire' : 'Local',
      clear: (s) => ({ ...s, wire: '' }),
    })
  }
  if (state.pipeline) {
    chips.push({
      key: 'pipeline',
      label: state.pipeline === 'A' ? 'A · digital' : 'B · scanned',
      clear: (s) => ({ ...s, pipeline: '' }),
    })
  }
  if (state.min_confidence) {
    chips.push({
      key: 'min_confidence',
      label: `≥ ${Math.round(Number(state.min_confidence) * 100)}%`,
      clear: (s) => ({ ...s, min_confidence: '' }),
    })
  }
  if (state.date_from || state.date_to) {
    chips.push({
      key: 'dates',
      label: `${state.date_from || '…'} to ${state.date_to || '…'}`,
      clear: (s) => ({ ...s, date_from: '', date_to: '' }),
    })
  }
  if (state.mode !== 'blend') {
    chips.push({ key: 'mode', label: state.mode, clear: (s) => ({ ...s, mode: 'blend' }) })
  }
  return chips
}

// --- the compatibility shim -------------------------------------------------

/**
 * A bbox in either notation. The contract says `[x0, y0, x1, y1]`; the older
 * response carries the IIIF string `x,y,w,h`.
 */
export function toRegion(value: Region | string | null | undefined): Region | null {
  if (!value) return null
  if (Array.isArray(value)) {
    return value.length === 4 && value.every((entry) => typeof entry === 'number')
      ? (value as Region)
      : null
  }
  const parts = String(value)
    .split(',')
    .map((part) => Number(part.trim()))
  if (parts.length !== 4 || parts.some((entry) => !Number.isFinite(entry))) return null
  return [parts[0], parts[1], parts[0] + parts[2], parts[1] + parts[3]]
}

type RawUnit = Partial<Omit<SearchUnitResult, 'region'>> & {
  region?: Region | string | null
}

const UNIT_TYPES: UnitType[] = ['article', 'photograph', 'advertisement', 'notice', 'listing']

function adaptUnit(raw: RawUnit): SearchUnitResult {
  const unitType: UnitType =
    raw.unit_type && UNIT_TYPES.includes(raw.unit_type) ? raw.unit_type : 'article'
  return {
    id: raw.id ?? '',
    unit_type: unitType,
    // The older shape has no separate unit and object, so the article's own id
    // is both. A link built from it still resolves.
    object_id: raw.object_id ?? raw.id ?? '',
    headline: raw.headline ?? null,
    deck: raw.deck ?? null,
    byline: raw.byline ?? null,
    pub_date: raw.pub_date ?? null,
    section: raw.section ?? null,
    section_label: raw.section_label ?? null,
    page_number: raw.page_number ?? null,
    page_id: raw.page_id ?? null,
    edition_id: raw.edition_id ?? '',
    route: raw.route ?? null,
    engine: raw.engine ?? null,
    word_count: raw.word_count ?? null,
    columns_label: raw.columns_label ?? null,
    is_wire: raw.is_wire ?? false,
    wire_service: raw.wire_service ?? null,
    confidence: raw.confidence ?? null,
    snippet: raw.snippet ?? null,
    rank: raw.rank ?? 0,
    region: toRegion(raw.region),
    crop_url: raw.crop_url ?? null,
    restricted: raw.restricted ?? false,
    semantic_only: raw.semantic_only ?? false,
    matched_layers: raw.matched_layers ?? [],
    // The page the unit was printed on, so a crop can be described as a
    // fraction of it without the enclosing page group being present.
    width_px: raw.width_px ?? null,
    height_px: raw.height_px ?? null,
    thumbnail_url: raw.thumbnail_url ?? null,
  }
}

type RawHit = {
  unit_id?: string
  unit_type?: UnitType
  region?: Region | string
  rank?: number
}

/**
 * A page group as this screen uses it.
 *
 * The response carries `thumbnail_url` per page and `SearchPageGroup` in
 * api.ts does not declare it yet, so it is added here rather than read off an
 * untyped object at the point of use. It is null when nothing was rasterised
 * for that page, which the row draws as a placeholder rather than as an image
 * that will not load.
 */
export type PageGroup = SearchPageGroup & { thumbnail_url: string | null }

type RawPageGroup = Partial<Omit<SearchPageGroup, 'units' | 'hit_regions'>> & {
  units?: RawUnit[]
  hit_regions?: RawHit[]
  thumbnail_url?: string | null
}

function adaptPageGroup(raw: RawPageGroup): PageGroup {
  const units = (raw.units ?? []).map(adaptUnit)
  const declared = (raw.hit_regions ?? [])
    .map((hit) => ({
      unit_id: hit.unit_id ?? '',
      unit_type: (hit.unit_type ?? 'article') as UnitType,
      region: toRegion(hit.region),
      rank: hit.rank ?? 0,
    }))
    .filter((hit) => hit.region !== null) as SearchPageGroup['hit_regions']
  // Falling back to the units' own regions is the same information under
  // another name, and it is what the page thumbnail draws.
  const fallback = units
    .filter((unit) => unit.region)
    .map((unit) => ({
      unit_id: unit.id,
      unit_type: unit.unit_type,
      region: unit.region as Region,
      rank: unit.rank,
    }))
  return {
    page_id: raw.page_id ?? '',
    edition_id: raw.edition_id ?? '',
    pub_date: raw.pub_date ?? null,
    page_number: raw.page_number ?? 0,
    section_label: raw.section_label ?? null,
    route: raw.route ?? null,
    width_px: raw.width_px ?? null,
    height_px: raw.height_px ?? null,
    publication_name: raw.publication_name ?? null,
    publication_slug: raw.publication_slug ?? null,
    hit_count: raw.hit_count ?? units.length,
    best_rank: raw.best_rank ?? units[0]?.rank ?? 0,
    restricted: raw.restricted ?? units.every((unit) => unit.restricted),
    hit_regions: declared.length > 0 ? declared : fallback,
    thumbnail_url: raw.thumbnail_url ?? null,
    units,
  }
}

/** Group a flat unit list by the page it was printed on, keeping rank order. */
function groupByPage(units: SearchUnitResult[]): PageGroup[] {
  const order: string[] = []
  const byPage = new Map<string, SearchUnitResult[]>()
  for (const unit of units) {
    const key = unit.page_id ?? `unit:${unit.id}`
    const bucket = byPage.get(key)
    if (bucket) {
      bucket.push(unit)
    } else {
      byPage.set(key, [unit])
      order.push(key)
    }
  }
  return order.map((key) => {
    const group = byPage.get(key) as SearchUnitResult[]
    const first = group[0]
    return adaptPageGroup({
      page_id: first.page_id ?? '',
      edition_id: first.edition_id,
      pub_date: first.pub_date,
      page_number: first.page_number ?? 0,
      section_label: first.section_label,
      route: first.route,
      // The units now carry their page's raster size, which is what the
      // thumbnail's hit regions are positioned against.
      width_px: first.width_px ?? null,
      height_px: first.height_px ?? null,
      thumbnail_url: first.thumbnail_url ?? null,
      hit_count: group.length,
      best_rank: Math.max(...group.map((unit) => unit.rank)),
      units: group as RawUnit[],
    })
  })
}

type RawSearch = Partial<Omit<SearchResponse, 'pages' | 'results'>> & {
  pages?: RawPageGroup[]
  results?: RawUnit[]
}

/**
 * Whatever the server answered, in the contract's shape.
 *
 * When the response is already page-grouped it passes through with its regions
 * normalised. When it is a flat list and pages were asked for, the list is
 * grouped here so the reader sees the same screen either way. The fields only
 * the server can know (the page's raster size, the whole-corpus page total)
 * stay null, and the interface omits them rather than guessing.
 */
export type AdaptedSearch = Omit<SearchResponse, 'pages'> & { pages?: PageGroup[] }

export function adaptSearch(
  raw: RawSearch | undefined,
  group: 'page' | 'unit',
): AdaptedSearch | undefined {
  if (!raw) return undefined
  // The server sends `pages: []` alongside a full `results` list when unit
  // grouping was asked for, so the presence of the key says nothing. What
  // decides is the grouping this screen asked for, and whether the server
  // filled the groups in.
  const serverGrouped = group === 'page' && Array.isArray(raw.pages) && raw.pages.length > 0
  const units = (raw.results ?? []).map(adaptUnit)
  const pages = serverGrouped
    ? (raw.pages ?? []).map(adaptPageGroup)
    : group === 'page'
      ? groupByPage(units)
      : undefined
  const totalUnits = raw.total_units ?? raw.total ?? units.length
  return {
    query: raw.query ?? '',
    group,
    total_units: totalUnits,
    total_pages: raw.total_pages ?? 0,
    total: raw.total ?? totalUnits,
    editions_searched: raw.editions_searched ?? 0,
    elapsed_ms: raw.elapsed_ms ?? 0,
    layers: raw.layers ?? { exact: 0, corrected: 0, fuzzy: 0, semantic: 0, phonetic: 0 },
    limit: raw.limit ?? PER_PAGE,
    offset: raw.offset ?? 0,
    pages,
    results: serverGrouped ? (pages ?? []).flatMap((page) => page.units) : units,
  }
}

type RawFacets = Partial<Facets> & { local?: number; wire?: number }

/** The same treatment for facets: present what was counted, omit the rest. */
export function adaptFacets(raw: RawFacets | undefined): Facets | undefined {
  if (!raw) return undefined
  return {
    query: raw.query ?? '',
    total: raw.total ?? 0,
    publications: raw.publications ?? [],
    sections: raw.sections ?? [],
    types: raw.types ?? [],
    authors: raw.authors ?? [],
    origin: raw.origin ?? { local: raw.local ?? 0, wire: raw.wire ?? 0 },
    pipeline: raw.pipeline ?? { A: 0, B: 0 },
    confidence_bands: raw.confidence_bands ?? [],
    years: raw.years ?? [],
    earliest: raw.earliest ?? null,
    latest: raw.latest ?? null,
    licence: raw.licence ?? null,
  }
}

// --- formatting -------------------------------------------------------------

const GROUPED = new Intl.NumberFormat('en-CA')

/** Every figure on this screen is counted, and every one of them is grouped. */
export function n(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : GROUPED.format(value)
}

export function seconds(ms: number | null | undefined): string | null {
  if (!ms) return null
  return `${(ms / 1000).toFixed(2)} s`
}

/**
 * The crop's size as a fraction of the page, which is what the board prints
 * under every crop. It needs the page's raster size; without it the pixel
 * measure is printed instead, because a fraction of an unknown page is not a
 * fact.
 */
export function cropMeasure(
  region: Region | null,
  widthPx: number | null | undefined,
  heightPx: number | null | undefined,
): string | null {
  if (!region) return null
  const width = region[2] - region[0]
  const height = region[3] - region[1]
  if (widthPx && heightPx) {
    return `${(width / widthPx).toFixed(3)} × ${(height / heightPx).toFixed(3)}`
  }
  return `${Math.round(width)} × ${Math.round(height)} px`
}
